import { Component, TemplateRef, ViewChild } from '@angular/core';
import {
  CtBinaryOperator,
  CtDatatableContainerFilterValues,
  CTModelDatatableFilter,
  CtModelConfiguration,
  CtModelRouteData,
  CtModelService,
  CtModelType,
  SnackbarService
} from '@ctsolution/ct-framework';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { CtWebapiGenericResponse } from '@ctsolution/ct-webapi';

import { LeadRouteData } from '../../../_core/route-data/lead.route-data';
import { CTConfigurationModel } from '../../../_core/classes/ct-configuration-model';
import { ILead } from '../../../_core/classes/lead';
import { CustomerTypeEnum } from '../../../_core/enum/customer-type.enum';
import { CtManagerService } from '../../../_core/lib/ct-manager.service';

import { LeadEditFormComponent } from './lead-edit-form/lead-edit-form.component';
import { LeadDocumentsConfiguration } from '../lead-documents/lead-documents.configuration';
import { SchedulerService } from '../../scheduler/scheduler.service';
import { SchedulerListConfiguration } from '../../scheduler/scheduler-list/scheduler-list.configuration';
import { LeadInformation } from '../lead-edit/lead-information-form/lead-information';
import { LeadInformationRouteData } from '../../../_core/route-data/lead-information.route-data';
import {
  LangDownloadFileConfiguration,
  WorkOrderRowsExportTypeVO
} from '../../lang-download-file/lang-download-file.configuration';
import { ReportDownloadParameter } from '../../../_core/classes/report-download.parameter';
import { WorkOrderService } from '../../work-order/work-order.service';

@Component({
  selector: 'app-lead-edit',
  templateUrl: './lead-edit.component.html',
  providers: [WorkOrderService]
})
export class LeadEditComponent {

  // ─── ViewChild ────────────────────────────────────────────────────────────

  @ViewChild(LeadEditFormComponent) leadEditFormComponent!: LeadEditFormComponent;
  @ViewChild('headerActions') headerActions: TemplateRef<any> | null = null;

  // ─── Configurazioni routing / model ───────────────────────────────────────

  leadModelRouteData: CtModelRouteData = LeadRouteData().setModelType(CtModelType.FORM);
  leadModelConfiguration: CtModelConfiguration<any> = CtModelConfiguration.create();

  // ─── Stato del componente ─────────────────────────────────────────────────

  leadOid: number | null = null;
  customerType: CustomerTypeEnum | null = null;
  showPlace = true;

  /**
   * Visibilità della sezione Work Order nel master-detail.
   * Può essere sovrascritta via override per-client (es. Waddi nasconde
   * i work order sui lead privati).
   */
  workOrderLeadConfiguration: CTConfigurationModel = CTConfigurationModel.create();
  workOrderLeadVisibleType: boolean = true;

  // ─── Configurazioni sezioni master-detail ────────────────────────────────

  datatableContainerFilterValues: CtDatatableContainerFilterValues | null = null;
  leadDocumentsConfiguration: LeadDocumentsConfiguration = LeadDocumentsConfiguration.create();
  schedulerListConfiguration: SchedulerListConfiguration | null = null;

  // ─── Configurazione pulsante download PDF ────────────────────────────────

  langDownloadFileConfiguration: LangDownloadFileConfiguration = LangDownloadFileConfiguration
    .create()
    .setColor('accent')
    .setIcon('picture_as_pdf')
    .setButtonType('mat-mini-fab')
    .setLanguage([
      {
        language: 'Italiano',
        code: 'it',
        type: 'IT',
        icon: 'it'
      }
    ]);

  // ─── Getter ───────────────────────────────────────────────────────────────

  /** Abilita le sezioni master-detail solo quando il lead è già salvato (ha un ID). */
  get enableMasterDetail(): boolean {
    return !!this.leadModelConfiguration?.RouteData?.id;
  }

  // ─────────────────────────────────────────────────────────────────────────

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackbar: SnackbarService,
    private workOrderService: WorkOrderService,
    private modelService: CtModelService<any>,
    private schedulerService: SchedulerService,
    private managerService: CtManagerService
  ) {
    this.leadModelConfiguration.setRouteData(this.leadModelRouteData);
    this.setupConfiguration();
  }

  ngAfterViewInit(): void {
    // Espone il template degli header actions alla pagina contenitore
    this.route.data.subscribe(data => {
      data['headerActions'] = this.headerActions;
    });
  }

  // ─── Setup iniziale ───────────────────────────────────────────────────────

  async setupConfiguration(): Promise<void> {
    this._readQueryParams();
    await this._initFromRouteId();
  }

  /** Legge i filtri contestuali passati dalla lista via query string (?q=...). */
  private _readQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      const q = params['q'];
      if (!q) return;

      const filterValues = JSON.parse(q);
      if (filterValues) {
        this.datatableContainerFilterValues = CtDatatableContainerFilterValues.create(filterValues);
      }
    });
  }

  /** Inizializza il componente quando è presente un ID lead nel route. */
  private async _initFromRouteId(): Promise<void> {
    const pId: string | null = this.route.snapshot.paramMap.get('id');
    if (!pId) return;

    this.leadOid = +pId;
    this.leadModelRouteData.setId(this.leadOid);
    this.leadDocumentsConfiguration?.setLeadOid(this.leadOid);

    await this._setupScheduler(this.leadOid);
    this._loadLeadData();
  }

  /** Configura lo scheduler-list se il modulo calendario è abilitato. */
  private async _setupScheduler(leadOid: number): Promise<void> {
    if (!await this.managerService.hasEnabled.calendar()) return;

    const param = this.schedulerService.returnParam(leadOid);

    this.schedulerListConfiguration = new SchedulerListConfiguration(
      [
        CTModelDatatableFilter
          .create()
          .setField('Lead.Oid')
          .setValue(leadOid)
          .setOperatorType(CtBinaryOperator.Equal)
      ],
      param
    )
      .setAction(() => this.submit(this.leadEditFormComponent?.configuration?.form ?? null))
      .setUseModal(true);
  }

  /** Carica i dati del lead dal server e inizializza il form e i metadati della pagina. */
  private _loadLeadData(): void {
    this.modelService
      .getInfos(this.leadModelConfiguration)
      .subscribe({
        next: async (response) => {
          const dataSource = response?.Result?.DataSource;
          if (!dataSource) return;

          // Popola il form con i dati ricevuti
          this.leadEditFormComponent.setup(dataSource);

          // Determina il tipo cliente per guidare la visibilità delle sezioni
          this.customerType = dataSource.Customer?.Type?.Type ?? dataSource.CustomerType?.Type;

          // Override per-client: alcuni client (es. Waddi) nascondono i work order sui lead privati
          if (this.customerType === CustomerTypeEnum.PRIVATE) {
            this.workOrderLeadVisibleType = false;
          }

          await this.managerService.getComponentOverride(
            'lead',
            'workOrderLeadConfiguration',
            this.workOrderLeadConfiguration
          );

          // Aggiorna il titolo della pagina in base al tipo cliente
          const title = (this.customerType === CustomerTypeEnum.BUSINESS)
            ? 'LeadBusinessButton'
            : 'LeadPrivateButton';

          this.route.data.subscribe(data => data['title'] = title);
        },
        error: (err) => console.error('Errore durante il recupero del lead:', err)
      });
  }

  // ─── Download PDF ─────────────────────────────────────────────────────────

  downloadPdf(lang: any): void {
    if (!this.leadOid) return;

    const parameter: ReportDownloadParameter = ReportDownloadParameter
      .create()
      .setLeadOid(this.leadOid)
      .setTemplateLanguage(lang.code ?? 'it')
      .setRowType(lang.rowType ?? WorkOrderRowsExportTypeVO.All)
      .setDirectDownload(false)
      .setOpenInBrowser(true)
      .setAction('LeadPdf')
      .setLabelDownload(`Lead${this.leadOid}`);

    this.workOrderService.downloadWorkOrderPDF(parameter);
  }

  // ─── Callback template ────────────────────────────────────────────────────

  /** Ricevuto da LeadEditFormComponent: mostra/nasconde la sezione dei luoghi. */
  onValueChanged(shouldShow: boolean): void {
    this.showPlace = shouldShow;
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  submit(formGroup: FormGroup | null): void {
    if (!formGroup) return;

    formGroup.markAllAsTouched();
    if (!formGroup.valid) return;

    // Estrae i valori LeadInformation dal form prima di costruire il DTO del lead.
    // I controlli LeadInformation sono identificati dalla leadInformationControlMap
    // e NON devono essere inclusi nel payload inviato al controller `lead`.
    const leadInformationControlMap = this.leadEditFormComponent?.leadInformationControlMap;
    const leadInformationValues = new Map<string, string>(); // code → content

    if (leadInformationControlMap?.size) {
      leadInformationControlMap.forEach((code, description) => {
        const content = formGroup.get(description)?.value ?? null;
        leadInformationValues.set(code, content);
        // Rimuove il campo dal valore grezzo del form per non inquinare il DTO del lead
        formGroup.removeControl(description);
      });
    }

    const value: ILead = formGroup.value as ILead;

    // Il LeadType può arrivare come oggetto con .Code (lookup): normalizziamo a codice stringa
    if ((value.LeadType as any)?.Code) {
      value.LeadType = (value.LeadType as any).Code;
    }

    // Mappiamo i tag all'array di Oid atteso dal backend
    const Tags: { Oid: number }[] = (value?.Tags ?? []).map(tag => ({ Oid: tag.value }));

    // Normalizziamo la data rimuovendo l'orario (mezzanotte UTC) per evitare
    // slittamenti di giorno causati dal fuso orario durante la serializzazione JSON
    let normalizedDate: Date | null = null;
    if (value.DocumentDate) {
      const d = new Date(value.DocumentDate);
      normalizedDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    }

    // Se il lead ha un Customer collegato, i campi anagrafici liberi non vanno inviati
    const hasCustomer = !!value.Customer;

    const formValue: any = {
      Customer:      hasCustomer ? { Oid: value.Customer.value } : null,
      CustomerType:  value.CustomerType  ? { Oid: value.CustomerType }  : null,
      DocumentDate:  normalizedDate,
      Email:         hasCustomer ? null : value.Email,
      LeadInterest:  value.LeadInterest  ? { Oid: value.LeadInterest }  : null,
      LeadSource:    value.LeadSource    ? { Oid: value.LeadSource }    : null,
      LeadStatus:    value.LeadStatus    ? { Oid: value.LeadStatus }    : null,
      LeadType:      value.LeadType      ? { Oid: value.LeadType }      : null,
      Name:          hasCustomer ? null : value.Name,
      Phone:         hasCustomer ? null : value.Phone,
      Surname:       hasCustomer ? null : value.Surname,
      CompanyPlace:  value.CompanyPlace  ? { Oid: value.CompanyPlace }  : null,
      Code:          value.Code,
      Note:          value.Note,
      VatNumber:     (value as any).VATNUMBER      ?? null,
      SdiCode:       (value as any).SDICODE        ?? null,
      BillingEmail:  (value as any).BillingEmail   ?? null,
      PaymentMethod: (value as any).PaymentMethod  ?? null,
      Prospect:      value.Prospect ?? null,
      Tags
    };

    this.modelService
      .putInfos(this.leadModelConfiguration, formValue)
      ?.subscribe({
        next: (response: CtWebapiGenericResponse<number | null>) => {

          // L'OID del lead è quello esistente (edit) oppure quello appena creato (insert)
          const leadOid: number = (response.Result && !isNaN(response.Result))
            ? response.Result
            : (this.leadOid ?? -1);

          // Salva i valori LeadInformation in parallelo, poi naviga/ricarica
          this._saveLeadInformation(leadOid, leadInformationValues).then(() => {

            this.snackbar.onSuccess();

            const commands = ['/', 'lead'];

            if (response.Result && !isNaN(response.Result)) {
              // Nuovo lead creato: naviga alla pagina di edit con l'OID restituito
              commands.push('edit', response.Result.toString());
              this.router.navigate(commands, { queryParamsHandling: 'preserve' });
            } else {
              // Lead esistente aggiornato: ricarica la configurazione
              this.setupConfiguration();
            }

          });

        },
        error: (err) => console.error('Errore durante il salvataggio del lead:', err)
      });
  }

  // ─── LeadInformation submit ───────────────────────────────────────────

  /**
   * Salva i valori LeadInformation su `leadinformation` controller.
   * Ogni campo diventa un record separato (PUT con Oid se già esiste, POST se nuovo).
   *
   * @param leadOid   OID del lead appena salvato
   * @param values    Mappa code → content estratta dal form
   * @returns Promise che si risolve quando tutti i PUT sono completati
   */
  private _saveLeadInformation(
    leadOid: number,
    values: Map<string, string>
  ): Promise<void> {

    if (!values.size) return Promise.resolve();

    const existingRecords: LeadInformation[] =
      this.leadEditFormComponent?.leadInformationDataSource ?? [];

    const requests: Array<Promise<void>> = [];

    values.forEach((content, code) => {

      const leadInformation = LeadInformation.create()
        .setLeadOid(leadOid)
        .setContent(content ?? '')
        .setType(code);

      const configuration = CtModelConfiguration.create()
        .setRouteData(
          CtModelRouteData.create()
            .setController(LeadInformationRouteData().controller!)
        );

      // Se esiste già un record con quel Type, usa il suo Oid per l'update.
      // Type è number nel modello, code è la stringa dal Map — convertiamo per confronto sicuro.
      const existing = existingRecords.find(
        (el: LeadInformation) => el.Type === parseInt(code, 10)
      );

      if (existing?.Oid) {
        configuration.RouteData?.setId(existing.Oid);
      }

      const request = new Promise<void>((resolve, reject) => {
        this.modelService
          .putInfos(configuration, leadInformation)
          ?.subscribe({
            next: () => resolve(),
            error: (err) => {
              console.error(`Errore salvataggio LeadInformation type=${code}:`, err);
              reject(err);
            }
          });
      });

      requests.push(request);

    });

    return Promise.all(requests).then(() => {});

  }

}
